import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GoogleMap, InfoWindow } from '@react-google-maps/api';
import { useMaps } from '@contexts/MapsContext';
import { Bike, ChevronRight, Map as MapIcon, List, Search, Star } from 'lucide-react';
import { ridersService } from '@services/riders.service';
import type { OnlineRider } from '@services/riders.service';
import { cn } from '@lib/utils';
import { PageGuard } from '@components/PageGuard';
import { ForceEndTripModal } from '@components/delivery/ForceEndTripModal';

const statusChip: Record<string, string> = {
  active:               'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  pending_verification: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  suspended:            'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  deactivated:          'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const availabilityChip: Record<string, string> = {
  online:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  on_delivery:  'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  offline:      'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

// §11c: a tier is ordered, so it rides the intensity ramp. `premium` was AMBER,
// i.e. --state-warning, which made every premium rider look like a problem in
// the fleet list. A tier is not a state.
//
// chekit (top) stays solid forest, premium steps down to progress-3, merchant is
// inert. The rank now reads as a magnitude, which is what it is.
const tierChip: Record<string, string> = {
  chekit:   'bg-[var(--brand-forest)] text-white',
  premium:  'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  merchant: 'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string) || '';

const MAP_CONTAINER = { width: '100%', height: '520px' };
const MAP_OPTIONS = {
  disableDefaultUI: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
} as const;
const NAIROBI = { lat: -1.2921, lng: 36.8219 };

export const RidersListPage = () => {
  const [view, setView] = useState<'list' | 'map'>('list');
  const [mapEverShown, setMapEverShown] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [availFilter, setAvailFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRider, setSelectedRider] = useState<OnlineRider | null>(null);
  const [forceEndRider, setForceEndRider] = useState<OnlineRider | null>(null);
  const [fleetMapReady, setFleetMapReady] = useState(false);
  const fleetMapRef = useRef<google.maps.Map | null>(null);
  const hasFitBoundsRef = useRef(false);
  const fleetMarkersRef = useRef<Map<string, google.maps.Marker>>(new Map());

  const onFleetMapLoad = useCallback((map: google.maps.Map) => {
    fleetMapRef.current = map;
    setFleetMapReady(true);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['riders', page, statusFilter, tierFilter, availFilter, search],
    queryFn: () =>
      ridersService.list({
        page,
        limit: 20,
        status: statusFilter || undefined,
        tier: tierFilter || undefined,
        availability: availFilter || undefined,
        search: search.trim() || undefined,
      }),
    refetchInterval: 6000,
  });

  const { data: onlineData } = useQuery({
    queryKey: ['riders-online'],
    queryFn: () => ridersService.getOnlineRiders(),
    refetchInterval: 6000,
    staleTime: 5000,
  });

  const { mapsLoaded } = useMaps();

  const total = data?.total ?? 0;
  const rows = Array.isArray(data?.data) ? data!.data : [];
  const totalPages = Math.max(1, Math.ceil(total / 20));

  const onlineRiders = onlineData?.data ?? [];
  const onlineCount     = onlineRiders.filter((r) => r.availability === 'online').length;
  const onDeliveryCount = onlineRiders.filter((r) => r.availability === 'on_delivery').length;

  // Fit map to all rider positions once; fires when map is ready AND riders data arrives.
  // Uses a ref so it only runs once; after that the user controls the viewport.
  useEffect(() => {
    if (!fleetMapReady || !fleetMapRef.current || hasFitBoundsRef.current) return;
    if (onlineRiders.length === 0) return;
    hasFitBoundsRef.current = true;
    if (onlineRiders.length === 1) {
      fleetMapRef.current.panTo({ lat: onlineRiders[0].latitude, lng: onlineRiders[0].longitude });
      fleetMapRef.current.setZoom(14);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      onlineRiders.forEach((r) => bounds.extend({ lat: r.latitude, lng: r.longitude }));
      fleetMapRef.current.fitBounds(bounds, 60);
    }
  }, [fleetMapReady, onlineRiders]);

  // Imperatively sync rider pins on the fleet map.
  // Runs whenever the map becomes ready or the online riders list updates (every 6s).
  // Using imperative markers avoids the @react-google-maps/api context timing race.
  useEffect(() => {
    if (!fleetMapReady || !fleetMapRef.current) return;
    const map = fleetMapRef.current;
    const markers = fleetMarkersRef.current;
    const liveIds = new Set(onlineRiders.map((r) => r.id));

    // Remove pins for riders no longer online
    markers.forEach((marker, id) => {
      if (!liveIds.has(id)) {
        marker.setMap(null);
        markers.delete(id);
      }
    });

    // Add or update pins for each online rider
    onlineRiders.forEach((r) => {
      const pos = { lat: r.latitude, lng: r.longitude };
      const label: google.maps.MarkerLabel = {
        text: r.availability === 'on_delivery' ? 'D' : 'R',
        // A literal, not var(--…), on purpose: Google Maps rasterises marker
        // labels itself and does not resolve CSS custom properties.
        color: '#ffffff',
        fontSize: '11px',
        fontWeight: '700',
      };

      if (markers.has(r.id)) {
        const marker = markers.get(r.id)!;
        marker.setPosition(pos);
        marker.setLabel(label);
        google.maps.event.clearListeners(marker, 'click');
        marker.addListener('click', () => setSelectedRider(r));
      } else {
        const marker = new google.maps.Marker({ map, position: pos, title: r.displayName, label });
        marker.addListener('click', () => setSelectedRider(r));
        markers.set(r.id, marker);
      }
    });
  }, [fleetMapReady, onlineRiders]);

  // Clean up all pins when the component unmounts
  useEffect(() => {
    return () => {
      fleetMarkersRef.current.forEach((m) => m.setMap(null));
      fleetMarkersRef.current.clear();
    };
  }, []);

  return (
    <PageGuard permission="riders.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              Platform
            </p>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Riders
            </h1>
            <p className="text-[14px] text-[var(--text-muted)]">
              Platform-wide rider fleet: merchant, chekit and premium tiers
            </p>
          </div>
          {/* View toggle */}
          <div className="flex items-center gap-1 rounded-2xl border border-[var(--border-default)] bg-white p-1">
            <button
              onClick={() => setView('list')}
              className={cn(
                'flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-medium transition-colors',
                view === 'list'
                  ? 'bg-[var(--brand-forest)] text-white'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              )}
            >
              <List className="w-3.5 h-3.5" strokeWidth={1.5} />
              List
            </button>
            <button
              onClick={() => { setView('map'); setMapEverShown(true); }}
              className={cn(
                'flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-medium transition-colors',
                view === 'map'
                  ? 'bg-[var(--brand-forest)] text-white'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              )}
            >
              <MapIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
              Live map
            </button>
          </div>
        </div>

        {/* Online summary bar */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 rounded-2xl bg-[var(--state-success-bg)] border border-[var(--state-success-border)] px-4 py-2.5">
            <span className="w-2 h-2 rounded-full bg-[var(--state-success)] animate-pulse" />
            <span className="text-[13px] font-semibold text-[var(--state-success)]">{onlineCount} online</span>
          </div>
          <div className="flex items-center gap-2 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-default)] px-4 py-2.5">
            <span className="w-2 h-2 rounded-full bg-[var(--brand-leaf)]" />
            <span className="text-[13px] font-semibold text-[var(--text-accent)]">{onDeliveryCount} on delivery</span>
          </div>
          <span className="text-[13px] text-[var(--text-muted)]">
            {total} total riders
          </span>
        </div>

        {/* ── FLEET MAP VIEW ───────────────────────────────────────────────────── */}
        {/* Keep GoogleMap mounted even when hidden; unmounting causes re-init failures */}
        <div style={{ display: view === 'map' ? 'block' : 'none' }}>
          <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
            <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-[var(--border-subtle)]">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                  Live fleet
                </p>
                <p className="text-[17px] font-semibold text-[var(--text-primary)] mt-0.5">
                  {onlineRiders.length} riders with location
                </p>
              </div>
              <span className="text-[12px] text-[var(--text-muted)]">Refreshes every 6s</span>
            </div>

            {!MAPS_API_KEY ? (
              <div className="h-[480px] flex items-center justify-center text-[13px] text-[var(--text-muted)] text-center px-6">
                Set <code className="font-mono mx-1">VITE_GOOGLE_MAPS_API_KEY</code> in{' '}
                <code className="font-mono ml-1">.env</code> to render the fleet map.
              </div>
            ) : !mapsLoaded || !mapEverShown ? (
              <div className="h-[480px] flex items-center justify-center text-[13px] text-[var(--text-muted)]">
                Loading map…
              </div>
            ) : (
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER}
                center={NAIROBI}
                zoom={12}
                options={MAP_OPTIONS}
                onClick={() => setSelectedRider(null)}
                onLoad={onFleetMapLoad}
              >
                {/* Rider pins are placed imperatively in the fleetMarkersRef effect above */}
                {selectedRider && (
                  <InfoWindow
                    position={{ lat: selectedRider.latitude, lng: selectedRider.longitude }}
                    onCloseClick={() => setSelectedRider(null)}
                  >
                    <div className="p-1 min-w-[160px]">
                      <p className="font-semibold text-[13px] text-[var(--text-primary)]">
                        {selectedRider.displayName}
                      </p>
                      {selectedRider.phone && (
                        <p className="text-[12px] text-[var(--text-muted)] mt-0.5">{selectedRider.phone}</p>
                      )}
                      <p className="text-[12px] text-[var(--text-muted)] capitalize mt-0.5">
                        {selectedRider.vehicleType} · {selectedRider.availability.replace('_', ' ')}
                      </p>
                      {selectedRider.lastLocationAt && (
                        <p className="text-[11px] text-[var(--text-muted)] mt-1">
                          {new Date(selectedRider.lastLocationAt).toLocaleTimeString()}
                        </p>
                      )}
                      <a
                        href={`/riders/${selectedRider.id}`}
                        className="mt-2 block text-[12px] font-medium text-[var(--text-primary)] underline"
                      >
                        View profile →
                      </a>
                      {selectedRider.availability === 'on_delivery' && (
                        <button
                          onClick={() => setForceEndRider(selectedRider)}
                          className="mt-2 w-full rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-3 py-1.5 text-[12px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)]"
                        >
                          Force end trip
                        </button>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            )}
          </div>

          <ForceEndTripModal
            open={!!forceEndRider}
            onClose={() => setForceEndRider(null)}
            riderId={forceEndRider?.id}
            subject={forceEndRider ? `${forceEndRider.displayName} · active delivery` : undefined}
          />
        </div>

        {/* ── LIST VIEW ────────────────────────────────────────────────────────── */}
        <div style={{ display: view === 'list' ? 'block' : 'none' }}>
          <>
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
                  strokeWidth={1.5}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search name, phone, email..."
                  className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
              >
                <option value="">All statuses</option>
                <option value="pending_verification">Pending verification</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="deactivated">Deactivated</option>
              </select>

              <select
                value={tierFilter}
                onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
                className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
              >
                <option value="">All tiers</option>
                <option value="merchant">Merchant</option>
                <option value="chekit">Chekit</option>
                <option value="premium">Premium</option>
              </select>

              <select
                value={availFilter}
                onChange={(e) => { setAvailFilter(e.target.value); setPage(1); }}
                className="rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
              >
                <option value="">All availability</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="on_delivery">On delivery</option>
              </select>
            </div>

            {/* Table */}
            <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
              {isLoading && (
                <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">Loading riders...</div>
              )}

              {error && (
                <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
                  Failed to load riders.
                </div>
              )}

              {!isLoading && !error && (
                <table className="w-full">
                  <thead className="bg-[var(--bg-base)]">
                    <tr>
                      <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Rider
                      </th>
                      <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Vehicle
                      </th>
                      <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Tier
                      </th>
                      <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Status
                      </th>
                      <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Availability
                      </th>
                      <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                        Performance
                      </th>
                      <th className="px-6 py-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {rows.map((r) => {
                      const fullName =
                        [r.userFirstName, r.userLastName].filter(Boolean).join(' ') || '—';
                      const isOnline = r.availability === 'online' || r.availability === 'on_delivery';
                      return (
                        <tr key={r.id} className="hover:bg-[var(--bg-base)] transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <Bike className="w-5 h-5 text-[var(--text-muted)]" strokeWidth={1.5} />
                                {isOnline && (
                                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--state-success)] border border-white" />
                                )}
                              </div>
                              <div>
                                <div className="text-[14px] font-medium text-[var(--text-primary)]">
                                  {fullName}
                                </div>
                                <div className="text-[12px] text-[var(--text-muted)]">
                                  {r.userPhone || r.userEmail || '—'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-[13px] text-[var(--text-primary)] capitalize">
                              {r.vehicleType || '—'}
                            </div>
                            {r.vehicleRegistration && (
                              <div className="text-[12px] text-[var(--text-muted)]">{r.vehicleRegistration}</div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={cn(
                                'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                                tierChip[r.riderTier] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                              )}
                            >
                              {r.riderTier}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={cn(
                                'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                                statusChip[r.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                              )}
                            >
                              {r.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5">
                              {isOnline && (
                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--state-success)] animate-pulse" />
                              )}
                              <span
                                className={cn(
                                  'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                                  availabilityChip[r.availability] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                                )}
                              >
                                {r.availability.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1 text-[13px] text-[var(--text-primary)]">
                              <Star
                                className="w-3.5 h-3.5 text-[var(--state-warning)]"
                                strokeWidth={1.5}
                                fill="var(--state-warning)"
                              />
                              {r.averageRating != null ? r.averageRating.toFixed(1) : '—'}
                              <span className="text-[12px] text-[var(--text-muted)]">({r.ratingCount})</span>
                            </div>
                            <div className="text-[12px] text-[var(--text-muted)]">
                              {r.totalDeliveries} deliveries
                              {r.isOnProbation && r.probationDeliveriesRemaining != null && (
                                <span className="ml-1">
                                  · {r.probationDeliveriesRemaining} left on probation
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Link
                              to={`/riders/${r.id}`}
                              className="w-8 h-8 rounded-full inline-flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
                            >
                              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}

                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]">
                          No riders match your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-[13px] text-[var(--text-muted)]">
                  Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-[13px] text-[var(--text-muted)] px-2">
                    Page {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
    </PageGuard>
  );
};
